/**
 * KnowledgeSourceTab — Manage knowledge sources and search indexes.
 *
 * Redesigned for clarity: card-based layout, inline environment buttons
 * instead of dropdowns, clear status indicators, and streamlined actions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw, Plus, Edit3, Save, X, Loader2,
  Database, Globe, FolderOpen, Play, CheckCircle, AlertTriangle, Search,
  Zap, Trash2, FileText, Clock, ExternalLink, Image as ImageIcon,
} from 'lucide-react';
import type { KnowledgeSource, GraphSearchIndex } from './types';
import { API_BASE, authFetch, getAgentDisplayName, formatRelativeTime } from './constants';
import { IMAGE_MODE_OPTIONS, isVisionHeavy, captionToImageMode, getImageModeOption, type ImageModeChoice } from './imageModeOptions';
import ImageModePicker from './ImageModePicker';

const SOURCE_TYPES = [
  { value: 'sharepoint_library', label: 'SharePoint Library' },
  { value: 'sharepoint_site_pages', label: 'SharePoint Site Pages' },
  { value: 'blob_storage', label: 'Azure Blob Storage' },
  { value: 'external_url', label: 'External URL / Website' },
];

const ENVIRONMENTS = ['dev', 'lab', 'uat', 'prod'] as const;

const ENV_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  dev: { bg: 'var(--idx-bg-inset)', text: '#3b82f6', border: '#3b82f6' },
  lab: { bg: 'var(--idx-bg-warn)', text: '#d97706', border: '#d97706' },
  uat: { bg: '#faf5ff', text: '#7c3aed', border: '#7c3aed' },
  prod: { bg: 'var(--idx-bg-success)', text: '#059669', border: '#059669' },
};

const STATUS_STYLES: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  active: { bg: '#dcfce7', text: '#166534', icon: <CheckCircle size={12} /> },
  indexing: { bg: '#fef3c7', text: '#92400e', icon: <Loader2 size={12} className="idx-spin" /> },
  error: { bg: '#fee2e2', text: '#991b1b', icon: <AlertTriangle size={12} /> },
  pending: { bg: '#fef3c7', text: '#92400e', icon: <Clock size={12} /> },
  paused: { bg: '#f1f2f4', text: '#6f747b', icon: <Clock size={12} /> },
  disabled: { bg: '#f1f2f4', text: '#6f747b', icon: <X size={12} /> },
};

interface KnowledgeSourceTabProps {
  onRefresh: () => void;
}

const KnowledgeSourceTab: React.FC<KnowledgeSourceTabProps> = ({ onRefresh }) => {
  const [sources, setSources] = useState<KnowledgeSource[]>([]);
  const [indexes, setIndexes] = useState<GraphSearchIndex[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [globalMessage, setGlobalMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [activeEnv, setActiveEnv] = useState<string>('dev');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [srcRes, idxRes] = await Promise.all([
        authFetch(`${API_BASE}/admin/graph/sources`, { credentials: 'include' }),
        authFetch(`${API_BASE}/admin/graph/search-indexes`, { credentials: 'include' }),
      ]);
      if (!srcRes.ok) throw new Error(`Failed to load sources: ${srcRes.status}`);
      if (!idxRes.ok) throw new Error(`Failed to load indexes: ${idxRes.status}`);
      setSources(await srcRes.json());
      setIndexes(await idxRes.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showMessage = (text: string, type: 'success' | 'error') => {
    setGlobalMessage({ text, type });
    setTimeout(() => setGlobalMessage(null), 6000);
  };

  const handleCreateSource = async (form: Record<string, unknown>) => {
    try {
      const res = await authFetch(`${API_BASE}/admin/graph/sources`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.error || `${res.status}`); }
      showMessage(`Source "${form.sourceId}" created`, 'success');
      setShowCreateForm(false);
      await fetchData();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Create failed', 'error');
    }
  };

  const handleUpdateSource = async (sourceId: string, props: Record<string, unknown>) => {
    try {
      const res = await authFetch(`${API_BASE}/admin/graph/sources/${sourceId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(props),
      });
      if (!res.ok) throw new Error(`Update failed: ${res.status}`);
      showMessage(`Source "${sourceId}" updated`, 'success');
      await fetchData();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Update failed', 'error');
    }
  };

  const handleDeleteSource = async (sourceId: string) => {
    if (!window.confirm(`Disable source "${sourceId}"?`)) return;
    try {
      await authFetch(`${API_BASE}/admin/graph/sources/${sourceId}`, { method: 'DELETE', credentials: 'include' });
      showMessage(`Source "${sourceId}" disabled`, 'success');
      await fetchData();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Delete failed', 'error');
    }
  };

  const handleProvisionIndex = async (indexId: string, environment: string) => {
    try {
      const res = await authFetch(`${API_BASE}/admin/graph/search-indexes/${indexId}/provision`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ environment }),
      });
      const data = await res.json();
      if (data.exists) {
        showMessage(`Index "${data.indexName}" already exists in ${environment}`, 'success');
      } else if (data.created) {
        showMessage(`Index "${data.indexName}" provisioned in ${environment}`, 'success');
      } else {
        showMessage(`Provision failed: ${data.error || 'Unknown'}`, 'error');
      }
      await fetchData();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Provision failed', 'error');
    }
  };

  const handleCreateAndLinkIndex = async (sourceId: string) => {
    try {
      const indexId = `askaldo-${sourceId.replace(/-sharepoint$/, '').replace(/[^a-z0-9-]/gi, '-')}`;
      const namePattern = `${indexId}-{env}`;

      const createRes = await authFetch(`${API_BASE}/admin/graph/search-indexes`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ indexId, namePattern, dimension: 3072, status: 'pending' }),
      });
      if (!createRes.ok) {
        const e = await createRes.json();
        throw new Error(e.error || `Create index failed: ${createRes.status}`);
      }

      const linkRes = await authFetch(`${API_BASE}/admin/graph/relationships`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fromLabel: 'KnowledgeSource', fromKey: 'sourceId', fromValue: sourceId,
          toLabel: 'SearchIndex', toKey: 'indexId', toValue: indexId,
          relType: 'INDEXES_INTO',
        }),
      });
      if (!linkRes.ok) {
        const e = await linkRes.json();
        throw new Error(e.error || `Link failed: ${linkRes.status}`);
      }

      showMessage(`Index "${indexId}" created and linked. Click Provision to create it in Azure Search.`, 'success');
      await fetchData();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Create & link failed', 'error');
    }
  };

  const handleTriggerIndexing = async (sourceId: string, environment: string, imageMode?: string, maxVisionCalls?: number) => {
    try {
      const res = await authFetch(`${API_BASE}/admin/graph/sources/${sourceId}/index`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ environment, mode: 'incremental', imageMode, maxVisionCalls }),
      });
      const data = await res.json();
      if (data.triggered) {
        const via = data.via === 'indexing_service' ? 'indexing service' : 'function app';
        const status = data.completed ? ' (completed)' : '';
        showMessage(`Indexing triggered via ${via} for ${data.indexName} in ${environment}${status}`, 'success');
      } else {
        showMessage(data.message || 'Not triggered — no indexing backend available.', 'error');
      }
      await fetchData();
    } catch (err) {
      showMessage(err instanceof Error ? err.message : 'Trigger failed', 'error');
    }
  };

  if (loading) return (
    <div className="idx-section">
      <div className="idx-loading"><Loader2 className="idx-spin" size={20} /> Loading knowledge sources...</div>
    </div>
  );

  if (error) return (
    <div className="idx-section">
      <div className="idx-error">
        <AlertTriangle size={20} /><span>{error}</span>
        <button onClick={fetchData} className="idx-btn idx-btn-sm">Retry</button>
      </div>
    </div>
  );

  const unlinkedIndexes = indexes.filter(i => i.sources.length === 0);

  return (
    <div className="idx-section">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--idx-text-primary)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 18, fontWeight: 700 }}>
            <Database size={22} style={{ color: 'var(--idx-text-link, #3b82f6)' }} /> Knowledge Sources
          </h2>
          <p style={{ margin: '4px 0 0', color: 'var(--idx-text-muted)', fontSize: 13 }}>
            {sources.length} source{sources.length !== 1 ? 's' : ''} · {indexes.length} search index{indexes.length !== 1 ? 'es' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <EnvSelector active={activeEnv} onChange={setActiveEnv} />
          <button onClick={() => setShowCreateForm(true)} className="idx-btn idx-btn-sm" style={{ background: '#10b981', color: 'white', border: 'none' }}>
            <Plus size={14} /> New Source
          </button>
          <button onClick={() => { fetchData(); onRefresh(); }} className="idx-btn idx-btn-sm">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Toast */}
      {globalMessage && (
        <div style={{
          padding: '10px 16px', marginBottom: 16, borderRadius: 8,
          background: globalMessage.type === 'error' ? 'var(--idx-bg-error)' : 'var(--idx-bg-success)',
          border: `1px solid ${globalMessage.type === 'error' ? '#fca5a5' : '#86efac'}`,
          color: 'var(--idx-text-primary)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
        }}>
          {globalMessage.type === 'error' ? <AlertTriangle size={16} style={{ color: '#ef4444' }} /> : <CheckCircle size={16} style={{ color: '#10b981' }} />}
          {globalMessage.text}
          <button onClick={() => setGlobalMessage(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--idx-text-muted)', padding: 2 }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Create Form */}
      {showCreateForm && (
        <CreateSourceForm
          onSubmit={handleCreateSource}
          onCancel={() => setShowCreateForm(false)}
          existingIds={sources.map(s => s.sourceId)}
        />
      )}

      {/* Source Cards */}
      {sources.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--idx-text-muted)', fontSize: 14 }}>
          No knowledge sources found. Create one or run the seed script.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sources.map(source => (
            <SourceCard
              key={source.sourceId}
              source={source}
              allIndexes={indexes}
              activeEnv={activeEnv}
              onUpdate={(props) => handleUpdateSource(source.sourceId, props)}
              onDelete={() => handleDeleteSource(source.sourceId)}
              onProvisionIndex={handleProvisionIndex}
              onTriggerIndexing={(env, imageMode, maxVisionCalls) => handleTriggerIndexing(source.sourceId, env, imageMode, maxVisionCalls)}
              onCreateAndLinkIndex={handleCreateAndLinkIndex}
              onShowMessage={showMessage}
              onRefresh={fetchData}
            />
          ))}
        </div>
      )}

      {/* Unlinked Indexes */}
      {unlinkedIndexes.length > 0 && (
        <div style={{ marginTop: 24, padding: 16, background: 'var(--idx-bg-secondary)', borderRadius: 10, border: '1px solid var(--idx-border)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--idx-text-muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Search size={14} /> Unlinked Indexes ({unlinkedIndexes.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {unlinkedIndexes.map(idx => (
              <span key={idx.indexId} style={{
                fontSize: 12, padding: '5px 10px', borderRadius: 6,
                background: 'var(--idx-bg-primary)', border: '1px solid var(--idx-border)',
                color: 'var(--idx-text-primary)', display: 'flex', alignItems: 'center', gap: 4,
                fontFamily: 'monospace',
              }}>
                <Search size={11} style={{ color: 'var(--idx-text-muted)' }} /> {idx.indexId}
                <span style={{ color: 'var(--idx-text-faint)' }}>({idx.namePattern})</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// Environment Selector (inline buttons, no dropdown)
// ============================================================================

const EnvSelector: React.FC<{ active: string; onChange: (env: string) => void }> = ({ active, onChange }) => (
  <div style={{ display: 'flex', gap: 2, borderRadius: 8, padding: 2, background: 'var(--idx-bg-secondary)', border: '1px solid var(--idx-border)' }}>
    {ENVIRONMENTS.map(env => {
      const isActive = active === env;
      const colors = ENV_COLORS[env];
      return (
        <button
          key={env}
          onClick={() => onChange(env)}
          style={{
            padding: '4px 12px', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600,
            cursor: 'pointer', transition: 'all 0.15s',
            textTransform: 'uppercase', letterSpacing: '0.03em',
            background: isActive ? colors.bg : 'transparent',
            color: isActive ? colors.text : 'var(--idx-text-faint)',
            boxShadow: isActive ? `0 0 0 1px ${colors.border}33` : 'none',
          }}
        >
          {env}
        </button>
      );
    })}
  </div>
);


// ============================================================================
// Create Source Form
// ============================================================================

const CreateSourceForm: React.FC<{
  onSubmit: (form: Record<string, unknown>) => void;
  onCancel: () => void;
  existingIds: string[];
}> = ({ onSubmit, onCancel, existingIds }) => {
  const [form, setForm] = useState({
    sourceId: '', displayName: '', description: '',
    sourceType: 'sharepoint_library',
    siteUrl: 'https://unifirst.sharepoint.com/sites/AskAldoKB',
    documentLibrary: 'Shared Documents',
    folderPath: '',
    includePatterns: '["*.pdf", "*.docx", "*.doc"]',
    excludePatterns: '[]',
    syncSchedule: JSON.stringify({ schedule: 'daily', schedule_time: '06:00', incremental: true }),
    status: 'active',
  });
  const [imageMode, setImageMode] = useState<ImageModeChoice>('default');
  const [maxVisionCalls, setMaxVisionCalls] = useState('');

  const idError = existingIds.includes(form.sourceId) ? 'Source ID already exists' : '';

  const handleSubmit = () => {
    // 'skip'/'marker' map to the legacy indexer's coarser image_handling
    // vocabulary as a best-effort fallback (only consulted if the primary
    // Function App path is unreachable — see graphAdmin.js). The modern
    // image_mode value (below) is what actually governs the real pipeline.
    const legacyImageHandling = imageMode === 'default' ? 'skip'
      : (imageMode === 'skip' || imageMode === 'marker') ? 'skip' : 'vision';
    onSubmit({
      ...form,
      processingConfig: JSON.stringify({
        chunking_strategy: 'markdown_header', chunk_size: 1000, chunk_overlap: 200,
        image_handling: legacyImageHandling,
      }),
      imageMode: captionToImageMode(imageMode) || '',
      maxVisionCalls: maxVisionCalls === '' ? '' : Number(maxVisionCalls),
    });
  };

  return (
    <div style={{
      border: '1px solid #10b981', borderRadius: 12, padding: 20, marginBottom: 16,
      background: 'var(--idx-bg-secondary)', boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.1)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: 'var(--idx-text-primary)', fontSize: 15, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Plus size={16} style={{ color: '#10b981' }} /> New Knowledge Source
        </h3>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--idx-text-muted)', padding: 4 }}>
          <X size={18} />
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <FormField label="Source ID *" error={idError}>
          <input value={form.sourceId} onChange={e => setForm({ ...form, sourceId: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') })} placeholder="hrpolicy-sharepoint" style={inputStyle} />
        </FormField>
        <FormField label="Display Name *">
          <input value={form.displayName} onChange={e => setForm({ ...form, displayName: e.target.value })} placeholder="HR Policy" style={inputStyle} />
        </FormField>
        <FormField label="Source Type">
          <select value={form.sourceType} onChange={e => setForm({ ...form, sourceType: e.target.value })} style={inputStyle}>
            {SOURCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </FormField>
        <FormField label="Folder Path *">
          <input value={form.folderPath} onChange={e => setForm({ ...form, folderPath: e.target.value })} placeholder="e.g. PolicyTech" style={inputStyle} />
        </FormField>
        <div style={{ gridColumn: '1 / -1' }}>
          <FormField label="Site URL *">
            <input value={form.siteUrl} onChange={e => setForm({ ...form, siteUrl: e.target.value })} placeholder="https://unifirst.sharepoint.com/sites/..." style={inputStyle} />
          </FormField>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <FormField label="Description">
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="What content does this source contain?" style={inputStyle} />
          </FormField>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <FormField label="Image Strategy — how images in these documents get indexed">
            <ImageModePicker
              value={imageMode}
              onChange={setImageMode}
              maxVisionCalls={maxVisionCalls}
              onMaxVisionCallsChange={setMaxVisionCalls}
            />
          </FormField>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          onClick={handleSubmit}
          disabled={!form.sourceId || !form.displayName || !form.siteUrl || !!idError}
          style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 600,
            background: '#10b981', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            opacity: (!form.sourceId || !form.displayName || !form.siteUrl || !!idError) ? 0.5 : 1,
          }}
        >
          <Plus size={14} /> Create Source
        </button>
        <button onClick={onCancel} className="idx-btn idx-btn-sm">Cancel</button>
      </div>
    </div>
  );
};


// ============================================================================
// Source Card (redesigned)
// ============================================================================

interface SourceCardProps {
  source: KnowledgeSource;
  allIndexes: GraphSearchIndex[];
  activeEnv: string;
  onUpdate: (props: Record<string, unknown>) => void;
  onDelete: () => void;
  onProvisionIndex: (indexId: string, env: string) => void;
  onTriggerIndexing: (env: string, imageMode?: string, maxVisionCalls?: number) => void;
  onCreateAndLinkIndex: (sourceId: string) => Promise<void>;
  onShowMessage: (text: string, type: 'success' | 'error') => void;
  onRefresh: () => Promise<void>;
}

// Per-source image-mode override persists in localStorage (same pattern as
// KnowledgeBaseTab) so a chosen re-index strategy survives a page reload —
// AA-220's whole point is testing/changing a strategy without a code deploy.
const LS_SOURCE_IMAGE_MODE_PREFIX = 'askaldo:idxAdmin:sourceImageMode:';
const LS_SOURCE_MAX_VISION_PREFIX = 'askaldo:idxAdmin:sourceMaxVisionCalls:';

const SourceCard: React.FC<SourceCardProps> = ({
  source, allIndexes, activeEnv, onUpdate, onDelete,
  onProvisionIndex, onTriggerIndexing, onCreateAndLinkIndex, onShowMessage, onRefresh,
}) => {
  const [creatingIndex, setCreatingIndex] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [indexing, setIndexing] = useState(false);
  const [showImageStrategy, setShowImageStrategy] = useState(false);
  const [editForm, setEditForm] = useState({
    displayName: source.displayName,
    description: source.description || '',
    siteUrl: source.siteUrl,
    documentLibrary: source.documentLibrary || 'Shared Documents',
    folderPath: source.folderPath || '',
    sourceType: source.sourceType,
  });

  const initialImageMode = (): ImageModeChoice => {
    try {
      const stored = window.localStorage.getItem(LS_SOURCE_IMAGE_MODE_PREFIX + source.sourceId);
      if (stored) return stored as ImageModeChoice;
    } catch { /* localStorage unavailable */ }
    return (source.imageMode && IMAGE_MODE_OPTIONS.some(o => o.value === source.imageMode)
      ? source.imageMode : 'default') as ImageModeChoice;
  };
  const initialMaxVisionCalls = (): string => {
    try {
      return window.localStorage.getItem(LS_SOURCE_MAX_VISION_PREFIX + source.sourceId)
        || (source.maxVisionCalls !== undefined ? String(source.maxVisionCalls) : '');
    } catch {
      return '';
    }
  };
  const [imageMode, setImageModeState] = useState<ImageModeChoice>(initialImageMode);
  const [maxVisionCalls, setMaxVisionCallsState] = useState<string>(initialMaxVisionCalls);

  const setImageMode = (choice: ImageModeChoice) => {
    setImageModeState(choice);
    try { window.localStorage.setItem(LS_SOURCE_IMAGE_MODE_PREFIX + source.sourceId, choice); } catch { /* ignore */ }
  };
  const setMaxVisionCallsValue = (value: string) => {
    setMaxVisionCallsState(value);
    try {
      if (value) window.localStorage.setItem(LS_SOURCE_MAX_VISION_PREFIX + source.sourceId, value);
      else window.localStorage.removeItem(LS_SOURCE_MAX_VISION_PREFIX + source.sourceId);
    } catch { /* ignore */ }
  };

  const linkedIndexes = source.indexes || [];
  const statusStyle = STATUS_STYLES[source.status] || STATUS_STYLES.pending;
  const resolvedIndexName = linkedIndexes[0]?.namePattern?.replace('{env}', activeEnv) || '—';

  const handleIndex = async () => {
    const cap = maxVisionCalls === '' ? undefined : Number(maxVisionCalls);
    if (isVisionHeavy(imageMode) && !window.confirm(
      `Index ${source.sourceId} with image strategy "${imageMode}"?\n\n` +
      `This runs Vision API calls per image (cached images are reused) and increases cost and index time.` +
      `${cap !== undefined ? ` Capped at ${cap} real Vision calls this run.` : ' No cap set — unlimited calls.'} Continue?`
    )) return;
    setIndexing(true);
    try {
      await onTriggerIndexing(activeEnv, captionToImageMode(imageMode), cap);
    } finally {
      setIndexing(false);
    }
  };

  return (
    <div style={{
      border: '1px solid var(--idx-border)', borderRadius: 12, background: 'var(--idx-bg-primary)',
      overflow: 'hidden', transition: 'border-color 0.15s, box-shadow 0.15s',
    }}>
      {/* Card Header */}
      <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <SourceTypeIcon type={source.sourceType} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, color: 'var(--idx-text-primary)', fontSize: 15 }}>
              {source.displayName}
            </span>
            <StatusBadge status={source.status} style={statusStyle} />
            {source.agents.length > 0 && (
              <span style={{ fontSize: 11, color: 'var(--idx-text-muted)', display: 'flex', alignItems: 'center', gap: 3, background: 'var(--idx-bg-secondary)', padding: '2px 8px', borderRadius: 4 }}>
                <Zap size={10} /> {source.agents.map(a => getAgentDisplayName(a)).join(', ')}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: 'var(--idx-text-faint)', fontFamily: 'monospace' }}>
              {source.sourceId}
            </span>
            {source.folderPath && (
              <span style={{ fontSize: 11, color: 'var(--idx-text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <FolderOpen size={10} /> {source.folderPath}
              </span>
            )}
            {linkedIndexes.length > 0 && (
              <span style={{ fontSize: 11, color: 'var(--idx-text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Search size={10} /> {resolvedIndexName}
              </span>
            )}
            {source.lastTriggeredAt && (
              <span style={{ fontSize: 11, color: 'var(--idx-text-faint)', display: 'flex', alignItems: 'center', gap: 3 }}>
                <Clock size={10} /> {formatRelativeTime(source.lastTriggeredAt)}
                {source.lastTriggeredEnv && <span style={{ textTransform: 'uppercase', fontWeight: 600 }}>({source.lastTriggeredEnv})</span>}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {linkedIndexes.length === 0 ? (
            <button
              onClick={async () => { setCreatingIndex(true); await onCreateAndLinkIndex(source.sourceId); setCreatingIndex(false); }}
              disabled={creatingIndex}
              className="idx-btn idx-btn-sm"
              style={{ fontSize: 12 }}
            >
              {creatingIndex ? <Loader2 size={12} className="idx-spin" /> : <Plus size={12} />}
              Create Index
            </button>
          ) : (
            <>
              <button
                onClick={() => onProvisionIndex(linkedIndexes[0].indexId, activeEnv)}
                className="idx-btn idx-btn-sm"
                title={`Provision index in ${activeEnv}`}
                style={{ fontSize: 12 }}
              >
                <Database size={12} /> Provision
              </button>
              <button
                onClick={() => setShowImageStrategy(!showImageStrategy)}
                className="idx-btn idx-btn-sm"
                style={{ fontSize: 12, color: showImageStrategy ? '#059669' : undefined }}
                title={`Image strategy: ${getImageModeOption(imageMode)?.label || imageMode}`}
              >
                <ImageIcon size={12} /> Images
              </button>
              <button
                onClick={handleIndex}
                disabled={indexing}
                style={{
                  padding: '5px 14px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 600,
                  background: '#10b981', color: 'white', cursor: indexing ? 'wait' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: 5, opacity: indexing ? 0.7 : 1,
                }}
                title={`Run indexing in ${activeEnv}`}
              >
                {indexing ? <Loader2 size={12} className="idx-spin" /> : <Play size={12} />}
                Index ({activeEnv})
              </button>
            </>
          )}
          <button
            onClick={() => setEditMode(!editMode)}
            className="idx-btn idx-btn-sm"
            style={{ padding: '5px 8px' }}
            title="Edit source"
          >
            <Edit3 size={13} />
          </button>
          <button
            onClick={onDelete}
            className="idx-btn idx-btn-sm"
            style={{ padding: '5px 8px', color: '#ef4444' }}
            title="Disable source"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* Image Strategy — collapsible, applies to the next Index run (and any
          re-index after) for this source. Persisted in localStorage so the
          choice survives a reload. See AA-220 / IMAGE_MODE_REFERENCE.md. */}
      {showImageStrategy && linkedIndexes.length > 0 && (
        <div style={{ borderTop: '1px solid var(--idx-border)', padding: '12px 18px', background: 'var(--idx-bg-secondary)' }}>
          <ImageModePicker
            value={imageMode}
            onChange={setImageMode}
            maxVisionCalls={maxVisionCalls}
            onMaxVisionCallsChange={setMaxVisionCallsValue}
            disabled={indexing}
            compact
          />
        </div>
      )}

      {/* Edit Form */}
      {editMode && (
        <div style={{ borderTop: '1px solid var(--idx-border)', padding: '14px 18px', background: 'var(--idx-bg-secondary)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <FormField label="Display Name">
              <input value={editForm.displayName} onChange={e => setEditForm({ ...editForm, displayName: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Type">
              <select value={editForm.sourceType} onChange={e => setEditForm({ ...editForm, sourceType: e.target.value as KnowledgeSource['sourceType'] })} style={inputStyle}>
                {SOURCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </FormField>
            <FormField label="Library">
              <input value={editForm.documentLibrary} onChange={e => setEditForm({ ...editForm, documentLibrary: e.target.value })} style={inputStyle} />
            </FormField>
            <FormField label="Folder Path">
              <input value={editForm.folderPath} onChange={e => setEditForm({ ...editForm, folderPath: e.target.value })} style={inputStyle} />
            </FormField>
          </div>
          <FormField label="Site URL">
            <input value={editForm.siteUrl} onChange={e => setEditForm({ ...editForm, siteUrl: e.target.value })} style={inputStyle} />
          </FormField>
          <FormField label="Description">
            <input value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })} style={{ ...inputStyle, marginTop: 8 }} />
          </FormField>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button onClick={() => { onUpdate(editForm); setEditMode(false); }} style={{ padding: '6px 16px', borderRadius: 6, border: 'none', fontSize: 13, fontWeight: 600, background: '#10b981', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
              <Save size={13} /> Save
            </button>
            <button onClick={() => setEditMode(false)} className="idx-btn idx-btn-sm"><X size={13} /> Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================================================
// Shared sub-components
// ============================================================================

const SourceTypeIcon: React.FC<{ type: string }> = ({ type }) => {
  const iconMap: Record<string, { icon: React.ReactNode; color: string }> = {
    sharepoint_library: { icon: <FolderOpen size={18} />, color: '#0078d4' },
    sharepoint_site_pages: { icon: <Globe size={18} />, color: '#0078d4' },
    blob_storage: { icon: <Database size={18} />, color: '#14b8a6' },
    external_url: { icon: <ExternalLink size={18} />, color: '#f59e0b' },
  };
  const { icon, color } = iconMap[type] || { icon: <FileText size={18} />, color: 'var(--idx-text-muted)' };
  return (
    <div style={{
      width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `${color}15`, color, flexShrink: 0,
    }}>
      {icon}
    </div>
  );
};

const StatusBadge: React.FC<{ status: string; style: { bg: string; text: string; icon: React.ReactNode } }> = ({ status, style: s }) => (
  <span style={{
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 12,
    background: s.bg, color: s.text, display: 'inline-flex', alignItems: 'center', gap: 4,
    textTransform: 'capitalize',
  }}>
    {s.icon} {status}
  </span>
);

const FormField: React.FC<{ label: string; error?: string; children: React.ReactNode }> = ({ label, error, children }) => (
  <label style={{ color: 'var(--idx-text-muted)', fontSize: 12, display: 'block' }}>
    {label}
    {children}
    {error && <span style={{ color: '#ef4444', fontSize: 11 }}>{error}</span>}
  </label>
);


// ============================================================================
// Shared styles
// ============================================================================

const inputStyle: React.CSSProperties = {
  display: 'block', width: '100%', padding: '7px 10px', borderRadius: 6, fontSize: 13,
  border: '1px solid var(--idx-border)', background: 'var(--idx-bg-primary)', color: 'var(--idx-text-primary)',
  marginTop: 3, boxSizing: 'border-box',
};

export { KnowledgeSourceTab };
export default KnowledgeSourceTab;
